import { authenticatedFetch } from "./authenticatedFetch";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import { fetchAllEvaluatedKeyPages } from "../utils/paginatedFetch";
import { clampDocumentFileName } from "../utils/documentTitle";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const UPLOADED_SIGNATURES_BASE_PATH =
  import.meta.env.VITE_UPLOADED_SIGNATURES_BASE_PATH ||
  "/documents/uploaded-signatures";

export const MAX_UPLOADED_PDF_BYTES = 5 * 1024 * 1024;
export const MAX_UPLOADED_STORAGE_BYTES = 100 * 1024 * 1024;

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export { formatBytes };

/**
 * @param {File[]} files
 * @returns {Promise<{ success: true; data: object } | { success: false; error: string }>}
 */
export async function uploadUploadedSignaturePdfs(files) {
  if (!files?.length) {
    return { success: false, error: "Selecione ao menos um PDF." };
  }

  const formData = new FormData();
  for (const file of files) {
    formData.append("file", file, clampDocumentFileName(file.name));
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${UPLOADED_SIGNATURES_BASE_PATH}/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const msg =
        data?.message ||
        formatApiErrorMessage(data, "Não foi possível enviar os PDFs.");
      return { success: false, error: msg };
    }

    return { success: true, data };
  } catch (error) {
    console.error("uploadUploadedSignaturePdfs:", error);
    return { success: false, error: "Erro de rede ao enviar os PDFs." };
  }
}

/**
 * @returns {Promise<{ success: true; data: object } | { success: false; error: string }>}
 */
export async function listUploadedSignatureDocumentsPage(
  lastEvaluatedKey = null,
  { pageSize = 20 } = {}
) {
  try {
    const params = new URLSearchParams();
    params.append("page_size", String(pageSize));
    if (lastEvaluatedKey) {
      params.append("last_evaluated_key", JSON.stringify(lastEvaluatedKey));
    }

    const response = await authenticatedFetch(
      `${API_BASE_URL}${UPLOADED_SIGNATURES_BASE_PATH}?${params.toString()}`,
      { method: "GET" }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        error: formatApiErrorMessage(data, "Erro ao carregar documentos enviados."),
      };
    }

    return {
      success: true,
      data: {
        items: data.items || [],
        lastEvaluatedKey: data.last_evaluated_key || null,
        count: data.count ?? (data.items || []).length,
        storage_used_bytes: data.storage_used_bytes,
        storage_limit_bytes: data.storage_limit_bytes,
        max_file_bytes: data.max_file_bytes,
      },
    };
  } catch (error) {
    console.error("listUploadedSignatureDocumentsPage:", error);
    return { success: false, error: "Erro de rede ao carregar a lista." };
  }
}

/**
 * @returns {Promise<{ success: true; data: object } | { success: false; error: string }>}
 */
export async function listUploadedSignatureDocuments() {
  let storageMeta = {};

  const result = await fetchAllEvaluatedKeyPages(async (lastEvaluatedKey) => {
    const page = await listUploadedSignatureDocumentsPage(lastEvaluatedKey);
    if (page.success && !lastEvaluatedKey) {
      storageMeta = {
        storage_used_bytes: page.data.storage_used_bytes,
        storage_limit_bytes: page.data.storage_limit_bytes,
        max_file_bytes: page.data.max_file_bytes,
      };
    }
    return page;
  });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    data: {
      items: result.data.items,
      count: result.data.count,
      last_evaluated_key: null,
      ...storageMeta,
    },
  };
}

/**
 * @param {string} jobId
 * @returns {Promise<{ success: true; data: { blob: Blob; fileName: string } } | { success: false; error: string }>}
 */
export async function downloadUploadedSignaturePdf(jobId) {
  if (!jobId) return { success: false, error: "Documento inválido." };

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${UPLOADED_SIGNATURES_BASE_PATH}/${encodeURIComponent(jobId)}/download`,
      { method: "GET" }
    );

    if (!response.ok) {
      let message = "Não foi possível baixar o PDF.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { success: false, error: message };
    }

    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const fileName = match?.[1] || "documento.pdf";

    return { success: true, data: { blob, fileName } };
  } catch (error) {
    console.error("downloadUploadedSignaturePdf:", error);
    return { success: false, error: "Erro de rede ao baixar o PDF." };
  }
}

/**
 * @param {string} jobId
 */
export async function deleteUploadedSignatureDocument(jobId) {
  if (!jobId) return { success: false, error: "Documento inválido." };

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${UPLOADED_SIGNATURES_BASE_PATH}/${encodeURIComponent(jobId)}`,
      { method: "DELETE" }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        error: formatApiErrorMessage(data, "Não foi possível excluir o documento."),
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error("deleteUploadedSignatureDocument:", error);
    return { success: false, error: "Erro de rede ao excluir o documento." };
  }
}

export function isUploadedSignatureDocument(doc) {
  if (!doc || typeof doc !== "object") return false;
  return doc.is_manual_signature === true || doc.isManualSignature === true;
}
